/**
 * Typed sequential IDs for layout elements and equipment instances.
 *
 * Display names stay separate (Rail1 / Stocker1 / EQ1). These IDs are the
 * stable internal keys: TRACK-1, ST-1, EQ-1, JUNCTION-1, SWITCH-1, NODE-1, …
 */

import type { ElementType } from '../types';
import type { EquipmentObjectType } from '../features/equipment/equipmentTypes';

export type EntityIdPrefix =
  | 'TRACK'
  | 'SWITCH'
  | 'JUNCTION'
  | 'OHT'
  | 'ST'
  | 'EQ'
  | 'STB'
  | 'OHB'
  | 'LIFTER'
  | 'PILLAR'
  | 'BEAM'
  | 'ROD'
  | 'AREA'
  | 'ZONE'
  | 'LABEL'
  | 'BUF'
  | 'LIFT'
  | 'UTIL'
  | 'ROOM'
  | 'OTHER'
  | 'NODE';

const ELEMENT_TYPE_PREFIX: Record<ElementType, EntityIdPrefix> = {
  'overhead-rail': 'TRACK',
  'overhead-curve': 'TRACK',
  'cross-rail': 'JUNCTION',
  'oht-vehicle': 'OHT',
  stocker: 'ST',
  stb: 'STB',
  ohb: 'OHB',
  lifter: 'LIFTER',
  'support-pillar': 'PILLAR',
  'horizontal-beam': 'BEAM',
  'suspension-rod': 'ROD',
  rectangle: 'AREA',
  circle: 'ZONE',
  text: 'LABEL',
};

const EQUIPMENT_TYPE_PREFIX: Record<EquipmentObjectType, EntityIdPrefix> = {
  equipment: 'EQ',
  stocker: 'ST',
  buffer: 'BUF',
  lift: 'LIFT',
  utility: 'UTIL',
  room: 'ROOM',
  other: 'OTHER',
};

const ENTITY_ID_RE = /^([A-Z]+)-(\d+)$/;

export function formatEntityId(prefix: EntityIdPrefix | string, n: number): string {
  return `${prefix}-${n}`;
}

export function parseEntityId(id: string): { prefix: string; sequence: number } | null {
  const match = id.trim().match(ENTITY_ID_RE);
  if (!match) return null;
  const sequence = Number(match[2]);
  if (!Number.isFinite(sequence) || sequence < 1) return null;
  return { prefix: match[1], sequence };
}

/** Prefix for a layout element. Connector rails use SWITCH; others follow type. */
export function prefixForElement(
  type: ElementType,
  railType?: string | null
): EntityIdPrefix {
  if (type === 'overhead-rail' && String(railType || '').toLowerCase() === 'connector') {
    return 'SWITCH';
  }
  if (type === 'cross-rail') return 'JUNCTION';
  return ELEMENT_TYPE_PREFIX[type] ?? 'OTHER';
}

export function prefixForEquipment(objectType: EquipmentObjectType): EntityIdPrefix {
  return EQUIPMENT_TYPE_PREFIX[objectType] ?? 'OTHER';
}

/** Port id: EQ-1-LP-1 / ST-1-LP-1 */
export function formatPortId(parentId: string, portIndex: number): string {
  return `${parentId}-LP-${portIndex}`;
}

/**
 * Next free `PREFIX-N` among existing ids.
 * `reserved` covers ids already issued in the current batch.
 */
export function computeNextEntityId(
  prefix: EntityIdPrefix | string,
  existingIds: Iterable<string>,
  reserved: ReadonlySet<string> = new Set()
): string {
  let max = 0;
  for (const id of existingIds) {
    const parsed = parseEntityId(id);
    if (parsed?.prefix === prefix && parsed.sequence > max) {
      max = parsed.sequence;
    }
  }
  for (const id of reserved) {
    const parsed = parseEntityId(id);
    if (parsed?.prefix === prefix && parsed.sequence > max) {
      max = parsed.sequence;
    }
  }

  let n = max + 1;
  let candidate = formatEntityId(prefix, n);
  while (reserved.has(candidate)) {
    n += 1;
    candidate = formatEntityId(prefix, n);
  }
  return candidate;
}

export type EntityIdAllocator = {
  next(prefix: EntityIdPrefix | string): string;
  nextForElement(type: ElementType, railType?: string | null): string;
  nextForEquipment(objectType: EquipmentObjectType): string;
};

/**
 * Batch-safe ID factory. Every issued id is reserved so one user action that
 * creates several elements never collides.
 */
export function createEntityIdAllocator(existingIds: Iterable<string>): EntityIdAllocator {
  const snapshot = new Set(Array.from(existingIds));
  const reserved = new Set<string>();

  const next = (prefix: EntityIdPrefix | string): string => {
    const id = computeNextEntityId(prefix, snapshot, reserved);
    reserved.add(id);
    snapshot.add(id);
    return id;
  };

  return {
    next,
    nextForElement: (type, railType) => next(prefixForElement(type, railType)),
    nextForEquipment: (objectType) => next(prefixForEquipment(objectType)),
  };
}

/** Collect layout + equipment ids for a shared uniqueness pool (esp. ST). */
export function collectExistingEntityIds(
  layoutElements: Iterable<{ id: string }>,
  equipmentInstances: Iterable<{ id: string }> = []
): string[] {
  const ids: string[] = [];
  for (const el of layoutElements) ids.push(el.id);
  for (const inst of equipmentInstances) ids.push(inst.id);
  return ids;
}
